import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

export const useStatistics = (userId) => {
    const [statistics, setStatistics] = useState({
        websitesAnalyzed: 0,
        matchesFound: 0,
        customTypes: 0,
        remainingCredits: 0,
        isLoading: true,
        error: null
    });

    const fetchStatistics = async () => {
        if (!userId) return;

        try {
            const { data: profileData, error: profileError } = await supabase
                .from('profiles')
                .select(`
                    credits,
                    total_active_custom_types
                `)
                .eq('id', userId)
                .single();

            if (profileError) throw profileError;

            const { data: recentAnalysis, error: analysisError } = await supabase
                .from('analyses_history')
                .select(`
                    websites_analyzed,
                    matches_found
                `)
                .eq('user_id', userId)
                .eq('status', 'completed')
                .order('timestamp', { ascending: false })
                .limit(1)
                .single();

            if (analysisError && analysisError.code !== 'PGRST116') {
                throw analysisError;
            }

            setStatistics({
                websitesAnalyzed: recentAnalysis?.websites_analyzed || 0,
                matchesFound: recentAnalysis?.matches_found || 0,
                customTypes: profileData.total_active_custom_types || 0,
                remainingCredits: profileData.credits || 0,
                isLoading: false,
                error: null
            });
        } catch (error) {
            setStatistics(prev => ({
                ...prev,
                isLoading: false,
                error: 'Failed to load statistics'
            }));
        }
    };

    useEffect(() => {
        fetchStatistics();

        const analysesChannel = supabase.channel('analyses_changes')
            .on('postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'analyses_history',
                    filter: `user_id=eq.${userId}`
                },
                fetchStatistics
            )
            .on('postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'analyses_history',
                    filter: `user_id=eq.${userId}`
                },
                fetchStatistics
            )
            .subscribe();

        const profilesChannel = supabase.channel('profiles_changes')
            .on('postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'profiles',
                    filter: `id=eq.${userId}`
                },
                fetchStatistics
            )
            .subscribe();

        return () => {
            analysesChannel.unsubscribe();
            profilesChannel.unsubscribe();
        };
    }, [userId]);

    return statistics;
};